import type React from "react";

import type { ConstraintListItemDto } from "@/api/types";
import { ChevronRightIcon } from "@/components/Icons/ChevronRightIcon";
import { UsersCheckIcon } from "@/components/Icons/UsersCheckIcon";
import { Badge } from "@/design-system/Badge";
import { buttonVariants } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";
import { getIconByAudienceType, mapAudienceByType, orderAudienceTypes } from "@/helpers/audience";
import { twResolve } from "@/helpers/tw-resolve";
import { useTranslation } from "@/translations";

type AudienceType = ConstraintListItemDto["type"];
type AudiencePreviewProps = {
  audience: ConstraintListItemDto[];
  truncate?: boolean;
} & (
  | {
      readOnly: true;
      onClick?: never;
    }
  | {
      readOnly?: false;
      onClick: () => void;
    }
);
export function AudiencePreview({ audience, readOnly, truncate, onClick }: AudiencePreviewProps): React.ReactNode {
  const { t } = useTranslation();
  const audienceGroupByType = mapAudienceByType(audience);
  const filteredAudience = Object.fromEntries(
    Object.entries(audienceGroupByType).filter(([, items]) => items.length > 0),
  ) as Record<ConstraintListItemDto["type"], ConstraintListItemDto[]>;
  const availableAudienceTypes = Object.keys(filteredAudience) as AudienceType[];

  const content = (
    <>
      <Inline as="span" align="center" gap="xs" wrap={false}>
        <UsersCheckIcon tone="secondary" />
        <span className="text-text-secondary">{t("common.entity.audience")}:</span>
      </Inline>
      <span className="flex min-w-0 items-center gap-space-xs">
        <span className="flex min-w-0 flex-wrap items-center gap-space-xs">
          {availableAudienceTypes.length === 0 && (
            <Badge variant="white">{t("component.audience-selector.default-audience.label")}</Badge>
          )}
          {availableAudienceTypes.length > 0 &&
            orderAudienceTypes(availableAudienceTypes).map((type, index) => {
              const items = filteredAudience[type];
              const label = items.map(({ key }) => key).join(", ");
              const icon = getIconByAudienceType(type);
              if (truncate && index === 1) {
                const amountAudienceOmitted = Object.keys(filteredAudience).length - 1;

                return (
                  <Badge key={type} variant="white">
                    {t("common.label.x-more", {
                      count: amountAudienceOmitted,
                    })}
                  </Badge>
                );
              } else if (truncate && index > 1) {
                return null;
              }

              return (
                <Badge key={type} variant="white" icon={icon}>
                  {label}
                </Badge>
              );
            })}
        </span>
        {!readOnly && <ChevronRightIcon />}
      </span>
    </>
  );

  if (readOnly) {
    return (
      <aside className="flex w-full max-w-full flex-wrap items-start justify-between gap-space-sm py-space-sm">
        {content}
      </aside>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={twResolve(
        buttonVariants({
          variant: "ghostSecondary",
          type: "button",
          fullWidth: true,
          pressAnimation: false,
          content: "body",
        }),
        "max-w-full overflow-visible rounded-lg! border border-border-secondary text-left text-clip whitespace-normal transition-colors hover:border-border-tertiary focus-visible:border-border-tertiary",
      )}
    >
      <span className="flex min-h-control-sm w-full flex-wrap items-center justify-between gap-space-sm p-space-sm">
        {content}
      </span>
    </button>
  );
}
